import { forwardRef, type ButtonHTMLAttributes } from "react";
import { LoaderCircle } from "lucide-react";

import { cn } from "../lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
/** `lg` matches the 40px page-header controls (tenant and time-range selectors). */
export type ButtonSize = "sm" | "md" | "lg" | "icon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingLabel?: string;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-action-primary text-action-primary-ink hover:bg-action-primary-hover",
  secondary: "border border-border bg-surface text-primary hover:bg-surface-elevated",
  ghost: "text-secondary hover:bg-surface-elevated hover:text-primary",
  danger: "bg-status-error-text text-surface hover:brightness-90",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "density-control-sm gap-1.5 rounded-control px-3 text-xs",
  md: "density-control-md gap-2 rounded-control px-4 text-sm",
  lg: "density-control-lg gap-2 rounded-control px-4 text-sm",
  icon: "density-square-sm rounded-control",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = "secondary",
    size = "md",
    loading = false,
    loadingLabel,
    disabled,
    children,
    type = "button",
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex shrink-0 items-center justify-center font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-50",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <LoaderCircle className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
      ) : null}
      {loading && loadingLabel ? loadingLabel : children}
    </button>
  );
});